import { useCallback, useMemo, useState } from "react";
import {
  DiversifyThemedCardValues,
  KeepThemedCardValues,
  PDFReportChartType,
  PDFReportValueType,
  ReportThemedCardConfig,
  ReportValueCardProps,
} from "helpers/types";
import { SingleValueCardType } from "constants/index";
import {
  DIVERSIFY_ALL_STOCKS_OPTION,
  StrategyReportThemedCardDescriptions,
  StrategyReportThemedCardLabels,
  StrategyReportThemedCardValuesLabels,
} from "helpers/constants";
import { useStrategyReportValue } from "hooks/useReportStorage";
import { useBeginningConcentration } from "hooks/useBeginningConcentration";
import {
  ALLOCATION_SERIES_CONCENTRATION,
  ALLOCATION_SERIES_DIVERSIFIED,
  FULL_ALLOCATION_PCT,
  allocationSlices,
} from "../Charts/CurrentAllocationChartReport";
import { STRATEGY_REPORT_DEFAULTS } from "./strategyReportDefaults";
import { KeepFiguresState } from "./keepFigures";
import {
  DiversifyStockConcentrations,
  beginningConcentrationFigure,
  concentrationRowLabel,
  currentConcentrationFigure,
  diversifyStockConcentrations,
  diversifyStockFigure,
} from "./diversifyFigures";
import HeaderAssetSelect from "./HeaderAssetSelect";

const ALLOCATION_CHART_TITLE = "Current Allocation";

type RowFigure = Pick<ReportValueCardProps, "value" | "unavailableReason">;

/**
 * The Diversify card: its five rows, the Bucket 1 stock switch in its header,
 * and the allocation donut's figure.
 *
 * Four of the five rows are CALCULATED; the fifth, Beginning Concentration,
 * keeps its own hook -- it is a definition rather than a figure, and it is
 * scoped to the report (reportScopeKey) so a baseline typed for one client is
 * never the next client's.
 *
 * The calculated rows take the localStorage hook every calculated row uses, a
 * run replacing whatever is under it: Overall Diversified Performance, the
 * buckets' aggregate weight-adjusted return (task 3346), the Tax Loss Harvest
 * Sleeve (task 2822), Current Concentration (task 3348) and Holdings (task
 * 3349). Each also keeps its own refusal in component state rather than
 * storage: the reason belongs to the RUN while the figure it explains is
 * persisted, so a reload must not resurrect a refusal from a run that is
 * gone. And they refuse independently, so one row going to N/A must not take
 * another with it.
 *
 * `keepShares` and `keepFiguresState` are the Keep card's: the stock switch
 * offers the same Bucket 1 assets, and the baseline row follows the same
 * card state. `hasStoredReport` is "a completed run has stored figures" --
 * see beginningConcentrationFigure.
 */
export const useDiversifyCard = ({
  reportScopeKey,
  keepShares,
  keepFiguresState,
  hasStoredReport,
}: {
  reportScopeKey: string;
  keepShares: KeepThemedCardValues | null;
  keepFiguresState: KeepFiguresState;
  hasStoredReport: boolean;
}) => {
  const [overallDiversifiedPerformance, setOverallDiversifiedPerformance] =
    useStrategyReportValue(
      "overall_diversified_performance_strategy",
      STRATEGY_REPORT_DEFAULTS.OVERALL_DIVERSIFIED_PERFORMANCE,
    );
  const [
    overallDiversifiedPerformanceUnavailable,
    setOverallDiversifiedPerformanceUnavailable,
  ] = useState<string | undefined>(undefined);
  // Calculated as of task 2822 -- the sleeve IS Bucket 3, so the row is that
  // bucket's own performance figure -- so a run replaces whatever is under
  // it. Its reason is held apart from the aggregate's even though the two
  // rows sit on one card, because the backend refuses each on its own terms
  // -- one row can show a number while the other shows a sentence.
  const [taxLossHarvestSleeve, setTaxLossHarvestSleeve] =
    useStrategyReportValue(
      "tax_loss_harvest_sleeve_strategy",
      STRATEGY_REPORT_DEFAULTS.TAX_LOSS_HARVEST_SLEEVE,
    );
  const [taxLossHarvestSleeveUnavailable, setTaxLossHarvestSleeveUnavailable] =
    useState<string | undefined>(undefined);
  // NOT useStrategyReportValue and NOT plain useState -- the hook carries the
  // "always 100%, but editable, and never the next client's baseline" rule
  // (operator, task 3141) and its own docstring says why each of those two
  // gets the lifetime wrong.
  const [
    beginningConcentration,
    setBeginningConcentration,
    beginningConcentrationTyped,
  ] = useBeginningConcentration(reportScopeKey);
  // The card's stock switch (operator, task 3508): the run's per-stock
  // concentrations, and which stock -- if any -- the card's two concentration
  // rows are currently measured for. Seeded at All Stocks, which is the
  // whole-of-Bucket-1 behaviour the card has always had.
  const [diversifyStocks, setDiversifyStocks] =
    useState<DiversifyStockConcentrations | null>(null);
  const [selectedDiversifyStock, setSelectedDiversifyStock] = useState(
    DIVERSIFY_ALL_STOCKS_OPTION,
  );
  // What the two rows show WHILE a stock is selected.
  //
  // Plain component state, never written through to either of the stores
  // the All Stocks figures live in, and that is the whole design of this
  // pair. The reader may type over a per-stock figure -- every row of
  // these cards takes an edit -- but the typed figure is DELIBERATELY NOT
  // REMEMBERED (operator, task 3508): switching stock, switching back, or
  // running the report again reseeds from the payload. Held in either
  // store it would be, and worse: `useStrategyReportValue` is one flat
  // object with no stock in its key, so a figure typed for AAPL would come
  // back under MSFT's name and reach a client's PDF as MSFT's opening
  // concentration -- the same failure as the Keep card's stale figures
  // (task 3155). Leaving it here also keeps the All Stocks figures
  // untouched underneath, so returning to All Stocks returns to the run's
  // own answer and the reader's own 100% baseline.
  const [stockBeginningConcentration, setStockBeginningConcentration] =
    useState<RowFigure>({ value: "" });
  const [stockCurrentConcentration, setStockCurrentConcentration] =
    useState<RowFigure>({ value: "" });
  const [currentConcentration, setCurrentConcentration] =
    useStrategyReportValue(
      "current_concentration_strategy",
      STRATEGY_REPORT_DEFAULTS.CURRENT_CONCENTRATION,
    );
  const [currentConcentrationUnavailable, setCurrentConcentrationUnavailable] =
    useState<string | undefined>(undefined);
  const [holdings, setHoldings] = useStrategyReportValue(
    "holdings_strategy",
    STRATEGY_REPORT_DEFAULTS.HOLDINGS,
  );
  const [holdingsUnavailable, setHoldingsUnavailable] = useState<
    string | undefined
  >(undefined);

  // Seeds both rows from the payload for one selection -- the ONE place a
  // per-stock figure is read, so the two rows can never be resolved on
  // different terms. All Stocks seeds nothing: those two rows are served
  // by the card's own stored concentration and the reader's baseline.
  const applyDiversifyStock = useCallback(
    (stocks: DiversifyStockConcentrations | null, symbol: string) => {
      if (symbol === DIVERSIFY_ALL_STOCKS_OPTION) return;
      setStockBeginningConcentration(
        diversifyStockFigure(
          stocks?.beginning_concentration_by_symbol ?? null,
          stocks?.beginning_concentration_by_symbol_unavailable ?? null,
          symbol,
        ),
      );
      setStockCurrentConcentration(
        diversifyStockFigure(
          stocks?.current_concentration_by_symbol ?? null,
          stocks?.current_concentration_by_symbol_unavailable ?? null,
          symbol,
        ),
      );
    },
    [],
  );

  const handleDiversifyStockChange = useCallback(
    (symbol: string) => {
      setSelectedDiversifyStock(symbol);
      applyDiversifyStock(diversifyStocks, symbol);
    },
    [diversifyStocks, applyDiversifyStock],
  );

  // The operator typing a figure IS the figure (operator, task 2822), so
  // the backend's reason no longer describes what the row shows and must
  // go with it -- left standing it would hang a sentence about a refused
  // calculation off a number the reader just entered, and the row would
  // carry that contradiction into the client's PDF.
  const handleTaxLossHarvestSleeveChange = useCallback(
    (value: number | string) => {
      setTaxLossHarvestSleeveUnavailable(undefined);
      setTaxLossHarvestSleeve(value);
    },
    [setTaxLossHarvestSleeve],
  );

  // Applies a completed run's Diversify payload. `keepCard` is the Keep card
  // the same run produced, whose assets are the stocks the switch offers.
  //
  // Every calculated row here is applied on the same terms. A null is NOT
  // written over the stored figure: a refusal must not destroy a hand-entered
  // number. It is not left standing in silence either -- the reason goes into
  // component state and the row renders N/A behind it, so a stored figure
  // cannot pass for this run's result. Each reason is set OUTSIDE its
  // figure's guard: a run that PRODUCED the figure must clear the previous
  // run's refusal, or the row keeps reading N/A over a figure this run
  // measured. And `!= null` rather than `!` throughout, so a genuine 0 is
  // applied rather than swallowed as falsy.
  //
  // Each row reads its OWN reason key: the plain `unavailable` is the
  // aggregate return's (types.ts says which is whose). The rows refuse
  // independently, and reading one key for several put a row into N/A every
  // time ANOTHER figure was the one the run could not produce.
  const applyRun = useCallback(
    (
      diversify: DiversifyThemedCardValues | undefined,
      keepCard: KeepThemedCardValues | null,
    ) => {
      // A genuine 0% concentration is an account that has fully diversified
      // out of the stock.
      if (diversify?.current_concentration != null) {
        setCurrentConcentration(diversify.current_concentration);
      }
      // The card's per-stock concentrations, and the selection they are
      // read under (task 3508). The previously picked stock is KEPT when
      // this run still carries it -- the reader is usually running the
      // same report over a new period and is asking about the same
      // stock -- and otherwise the card falls back to All Stocks rather
      // than to a stock nobody chose. The same rule, for the same reason,
      // as the Keep card's option.
      //
      // Reseeding through applyDiversifyStock is also what DISCARDS a
      // per-stock figure the reader had typed: it belongs to the run this
      // one has just replaced.
      const stocks = diversifyStockConcentrations(diversify);
      setDiversifyStocks(stocks);
      const stockOption = keepCard?.assets[selectedDiversifyStock]
        ? selectedDiversifyStock
        : DIVERSIFY_ALL_STOCKS_OPTION;
      setSelectedDiversifyStock(stockOption);
      applyDiversifyStock(stocks, stockOption);
      setCurrentConcentrationUnavailable(
        diversify?.current_concentration_unavailable ?? undefined,
      );
      // `!= null` carries more weight on Holdings than on the percentages
      // beside it: a count of 0 is this client holding nothing in either
      // bucket, which is an ANSWER, and it is also what every absent value
      // the numeric pipeline coerces comes out as -- so a falsy test would
      // drop the real 0 and leave the row on the seed, telling an operator to
      // run the report they had just run.
      if (diversify?.holdings != null) {
        setHoldings(diversify.holdings);
      }
      setHoldingsUnavailable(diversify?.holdings_unavailable ?? undefined);
      // Bucket 2 and Bucket 3's aggregate return (task 3573 excluded
      // Bucket 1 from its terms) -- a pair that genuinely broke even is
      // the 0% it is.
      if (diversify?.overall_diversified_performance != null) {
        setOverallDiversifiedPerformance(
          diversify.overall_diversified_performance,
        );
      }
      setOverallDiversifiedPerformanceUnavailable(
        diversify?.unavailable ?? undefined,
      );
      // A Bucket 3 that genuinely went nowhere is the 0% it is.
      if (diversify?.tax_loss_harvest_sleeve != null) {
        setTaxLossHarvestSleeve(diversify.tax_loss_harvest_sleeve);
      }
      setTaxLossHarvestSleeveUnavailable(
        diversify?.tax_loss_harvest_sleeve_unavailable ?? undefined,
      );
    },
    [
      setCurrentConcentration,
      selectedDiversifyStock,
      applyDiversifyStock,
      setHoldings,
      setOverallDiversifiedPerformance,
      setTaxLossHarvestSleeve,
    ],
  );

  // A refusal describes ONE RUN, and a change of inputs has just retired it
  // -- so the sentence goes with it. Left standing, the row read "N/A, this
  // run produced no Bucket 1 stock value" for inputs no run has been made
  // against yet, which is a refusal reported before anything was refused;
  // the stored figure underneath comes back instead, which is what every
  // other calculated row on this page does after an input change.
  const resetForNewInputs = useCallback(() => {
    setCurrentConcentrationUnavailable(undefined);
  }, []);

  // The row's two props and the donuts' figure, resolved ONCE -- see
  // currentConcentrationFigure for why the row and the chart may not decide
  // this separately. Resolved here rather than at the call sites below so
  // the screen donut and the PDF's read the same value too.
  //
  // The donut plots a share of 100 and cannot draw a seed, which is why the
  // figure is null rather than 0 whenever there is none: a 0 fallback would
  // turn a "No Report" row into a complete ring asserting a fully
  // diversified account -- a made-up figure in a chart instead of in a
  // field.
  const currentConcentrationRow = useMemo(
    () =>
      currentConcentrationFigure(
        currentConcentration,
        currentConcentrationUnavailable,
      ),
    [currentConcentration, currentConcentrationUnavailable],
  );
  const currentConcentrationPct = currentConcentrationRow.pct;

  // The stocks the card's switch offers: the Bucket 1 assets THIS run
  // produced, which is the same list the Keep card's own switch is built
  // from and so the one place "the stocks in Bucket 1" is decided. Read
  // from the Keep card rather than from the concentration maps so that a
  // run whose concentration was REFUSED still offers its stocks -- the
  // reader then gets an explained N/A for the stock they asked about,
  // instead of a dropdown that silently lost its options.
  const diversifyStockOptions = useMemo(
    () => Object.keys(keepShares?.assets ?? {}),
    [keepShares],
  );

  // The card's rows for one stock SELECTION, because this card is rendered
  // twice from two different ones: the screen shows whatever the switch
  // says, and the client's PDF always shows the whole of Bucket 1 (task
  // 3508). One builder rather than two lists, so the three rows the switch
  // does not touch cannot come to differ between paper and screen.
  const diversifyRows = useCallback(
    (stock: string): ReportValueCardProps[] => {
      const stockSelected = stock !== DIVERSIFY_ALL_STOCKS_OPTION;
      return [
        {
          label:
            StrategyReportThemedCardValuesLabels.OVERALL_DIVERSIFIED_PERFORMANCE,
          // The empty sentinel WITH the reason, the pair every refused figure
          // on these cards renders: a muted "N/A" behind an info icon, the row
          // dropped from the client-facing PDF, and the stored figure waiting
          // underneath rather than passing for this run's answer -- which on
          // a PERCENTAGE row would have read as "the portfolio went precisely
          // nowhere". The Sleeve and Holdings rows below do the same.
          value: overallDiversifiedPerformanceUnavailable
            ? ""
            : overallDiversifiedPerformance,
          unavailableReason: overallDiversifiedPerformanceUnavailable,
          type: SingleValueCardType.PERCENTAGE,
          onValueChange: setOverallDiversifiedPerformance,
        },
        {
          label: StrategyReportThemedCardValuesLabels.TAX_LOSS_HARVEST_SLEEVE,
          value: taxLossHarvestSleeveUnavailable ? "" : taxLossHarvestSleeve,
          unavailableReason: taxLossHarvestSleeveUnavailable,
          type: SingleValueCardType.PERCENTAGE,
          onValueChange: handleTaxLossHarvestSleeveChange,
        },
        {
          // Both concentration rows name the stock they were measured for
          // when one is selected (task 3508), and keep the plain label on All
          // Stocks -- which is the selection, the figure and the meaning the
          // card has always had.
          label: concentrationRowLabel(
            StrategyReportThemedCardValuesLabels.BEGINNING_CONCENTRATION,
            stock,
          ),
          // All Stocks is the reader's own 100% baseline and stays on its own
          // hook, shown once a report has run (beginningConcentrationFigure
          // says when); a selected stock reads the run's figure out of
          // component state, where a typed override deliberately does not
          // outlive the selection (the state's own comment says why).
          ...(stockSelected
            ? stockBeginningConcentration
            : beginningConcentrationFigure(
                beginningConcentration,
                beginningConcentrationTyped,
                keepFiguresState,
                hasStoredReport,
              )),
          type: SingleValueCardType.PERCENTAGE,
          onValueChange: stockSelected
            ? (value: number | string) =>
                setStockBeginningConcentration({ value })
            : setBeginningConcentration,
        },
        {
          label: concentrationRowLabel(
            StrategyReportThemedCardValuesLabels.CURRENT_CONCENTRATION,
            stock,
          ),
          // All Stocks: the figure and any refusal together, from the one
          // resolution the donut beside this row reads as well. `pct` is the
          // donut's half of that answer and is not a card prop, so it is
          // dropped here.
          //
          // A selected stock does NOT feed that donut and is not read through
          // the same resolution: the chart stays the whole-of-Bucket-1
          // allocation whatever the dropdown says (operator, task 3508), so
          // the two answers it has to agree with are the stored figure and
          // the stored reason -- exactly the ones it already reads.
          ...(stockSelected
            ? stockCurrentConcentration
            : {
                value: currentConcentrationRow.value,
                unavailableReason: currentConcentrationRow.unavailableReason,
              }),
          type: SingleValueCardType.PERCENTAGE,
          onValueChange: stockSelected
            ? (value: number | string) =>
                setStockCurrentConcentration({ value })
            : setCurrentConcentration,
        },
        {
          label: StrategyReportThemedCardValuesLabels.HOLDINGS,
          value: holdingsUnavailable ? "" : holdings,
          unavailableReason: holdingsUnavailable,
          type: SingleValueCardType.NUMBER,
          onValueChange: setHoldings,
        },
      ];
    },
    [
      overallDiversifiedPerformance,
      overallDiversifiedPerformanceUnavailable,
      setOverallDiversifiedPerformance,
      taxLossHarvestSleeve,
      taxLossHarvestSleeveUnavailable,
      handleTaxLossHarvestSleeveChange,
      stockBeginningConcentration,
      beginningConcentration,
      beginningConcentrationTyped,
      keepFiguresState,
      hasStoredReport,
      setBeginningConcentration,
      stockCurrentConcentration,
      currentConcentrationRow,
      setCurrentConcentration,
      holdings,
      holdingsUnavailable,
      setHoldings,
    ],
  );

  // The PDF donut's two slices, clamped once here -- one call, so the pair
  // cannot be built from two different readings of the same figure.
  const allocationPdfSlices = useMemo(
    () =>
      currentConcentrationPct === null
        ? null
        : allocationSlices(currentConcentrationPct),
    [currentConcentrationPct],
  );

  const card = useMemo((): ReportThemedCardConfig => {
    const values = diversifyRows(selectedDiversifyStock);
    return {
      label: StrategyReportThemedCardLabels.DIVERSIFY,
      description: StrategyReportThemedCardDescriptions.DIVERSIFY,
      values,
      // What the client's PDF prints: THE SAME ROWS BUILT FOR ALL STOCKS,
      // whatever the switch says on screen (operator, task 3508).
      //
      // The stock switch is a screen-side lens and the export is the one
      // document that leaves the building, so a figure measured for one stock
      // must never print there labelled as the whole bucket -- beside a donut
      // still drawn from the all-stocks figure, which would leave the paper
      // disagreeing with itself. The BUILDER again rather than a second list:
      // the three rows the switch does not touch are then the same rows by
      // construction, and a row added to this card reaches paper and screen
      // together or not at all.
      pdfValues:
        selectedDiversifyStock !== DIVERSIFY_ALL_STOCKS_OPTION
          ? diversifyRows(DIVERSIFY_ALL_STOCKS_OPTION)
          : values,
      // Which Bucket 1 stock the card's two concentration rows are measured
      // for, or All Stocks for the whole of the bucket (task 3508). Absent
      // when the run produced no Bucket 1 asset: there is nothing to pick
      // between, which is what an unrendered select says -- the same rule
      // the Keep and Protect switches follow.
      headerAction: diversifyStockOptions.length ? (
        <HeaderAssetSelect
          value={selectedDiversifyStock}
          options={[DIVERSIFY_ALL_STOCKS_OPTION, ...diversifyStockOptions]}
          onChange={handleDiversifyStockChange}
        />
      ) : undefined,
      addictional_component: {
        type: PDFReportChartType.DONUT,
        title: ALLOCATION_CHART_TITLE,
        limit: FULL_ALLOCATION_PCT,
        data: [
          // Empty when there is no figure: the PDF's donut takes numbers,
          // and a 0/100 ring on paper asserts an allocation nobody
          // calculated -- the same false claim the screen's chart refuses.
          // Both slices come from the screen chart's own allocationSlices,
          // so a concentration over 100% clamps to the same ring on paper
          // as it does on screen rather than reaching a client's PDF as a
          // negative arc.
          ...(allocationPdfSlices === null
            ? []
            : [
                {
                  label: ALLOCATION_SERIES_CONCENTRATION,
                  value: allocationPdfSlices.concentration,
                  type: PDFReportValueType.PERCENTAGE,
                },
                {
                  label: ALLOCATION_SERIES_DIVERSIFIED,
                  value: allocationPdfSlices.diversified,
                  type: PDFReportValueType.PERCENTAGE,
                },
              ]),
        ],
      },
    };
  }, [
    diversifyRows,
    selectedDiversifyStock,
    diversifyStockOptions,
    handleDiversifyStockChange,
    allocationPdfSlices,
  ]);

  return {
    card,
    currentConcentrationPct,
    currentConcentrationUnavailable,
    applyRun,
    resetForNewInputs,
  };
};
